import React, {h, Component} from 'preact';
import {PropTypes} from 'preact-compat';
import RangeSlider from '../RangeSlider';
import './AudioPlayer.scss';

function timeZeroPad(n) {
    return (n >= 10 ? n : '0' + n);
}

function formatTime(timeInSeconds) {
    var roundedSeconds = Math.round(timeInSeconds);
    var minutes = Math.floor(roundedSeconds / 60);
    var seconds = roundedSeconds % 60;
    return timeZeroPad(minutes) + ':' + timeZeroPad(seconds);
}

export default class AudioPlayer extends Component {
    static get propTypes() {
        return {
            src: PropTypes.string,
            onMediaButtonClick: PropTypes.func
        };
    }

    constructor(props) {
        super(props);

        let sourceUrl = this.props.src;
        if (/(.*twilio\.com.*\/RE[^/]+)/.test(sourceUrl)) {
            /**
             * Changing the twilio audio SRC to use .wav instead of mp3, for the .mp3 addresses we
             * already have on the database, because .mp3 files cause issues with playback,
             * given the inability to get the duration of the audio. However we must keep mp3 for IE 11
             * .wav compatibility chart, for reference https://caniuse.com/#search=wav
             */
            var isIE = !!window.MSInputMethodContext && !!document.documentMode;
            var extension = isIE ? '.mp3' : '.wav';
            sourceUrl = sourceUrl.replace(/\.\w+$/, '') + extension;
        }

        this.state = {
            sourceUrl,
            isPlaying: false,
            isSliding: false,
            currentTime: 0,
            duration: 0
        };

        this.audioRef = React.createRef();
    }

    triggerOnMediaButtonClick(button) {
        this.props.onMediaButtonClick && this.props.onMediaButtonClick(button);
    }

    updateTime() {
        if (!this.audio) {
            return;
        }
        const {currentTime, duration} = this.audio;
        this.setState({
            duration: duration != null && isFinite(duration) ? duration : 0,
            currentTime: currentTime != null && !isNaN(currentTime) ? currentTime : 0
        });
    }

    componentDidMount() {
        const audio = new MediaElement(this.audioRef.current, {
            pluginPath: 'https://cdnjs.com/libraries/mediaelement/4.2.10/',
            success: mediaElement => {
                mediaElement.load();
            }
        });
        audio.addEventListener('loadedmetadata', () => {
            this.updateTime();
        });
        audio.addEventListener('timeupdate', () => {
            if (!this.state.isSliding) {
                this.updateTime();
            }
        });
        audio.addEventListener('play', () => {
            this.setState({isPlaying: true});
        });
        audio.addEventListener('pause', () => {
            this.setState({isPlaying: false});
        });
        audio.addEventListener('ended', () => {
            this.updateTime();
            this.setState({isPlaying: false, isSliding: false});
            audio.currentTime = 0;
            audio.pause();
        });
        this.audio = audio;
    }

    onPlayClick() {
        this.audio.play();
        this.triggerOnMediaButtonClick('play');
    }

    onPauseClick() {
        this.audio.pause();
        this.triggerOnMediaButtonClick('pause');
    }

    onReplayClick() {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.play();
        this.triggerOnMediaButtonClick('replay');
    }

    onSliderInput(event) {
        this.setState({
            isSliding: true,
            currentTime: this.state.duration * event.target.value / 100
        });
    }

    onSliderChange(event) {
        this.setState({isSliding: false});
        if (this.audio.duration) {
            this.audio.currentTime = this.audio.duration * event.target.value / 100;
        }
    }

    render() {
        const {sourceUrl, currentTime, duration} = this.state;
        const currentProgress = duration ? (currentTime / duration * 100) : 0;

        return (
            <div className="audio-player">
                <audio ref={this.audioRef} preload="auto">
                    <source src={sourceUrl}/>
                </audio>
                <div className="progress-slider">
                    <RangeSlider
                        value={currentProgress}
                        min={0}
                        max={100}
                        step={1}
                        onInput={this.onSliderInput.bind(this)}
                        onChange={this.onSliderChange.bind(this)}/>
                    <div className="time">
                        <span className="status-normal time-current">{formatTime(currentTime)}</span>
                        {' / '}
                        <span className="status-secondary time-total">{formatTime(duration)}</span>
                    </div>
                </div>
                {this.state.isPlaying ?
                    <button type="button" className="media-button media-button-pause" onClick={this.onPauseClick.bind(this)}>
                        <img className="icon" src={global.imageBaseUrl + 'proposal/media_pause.svg'}/>
                    </button>
                    :
                    <button type="button" className="media-button media-button-play" onClick={this.onPlayClick.bind(this)}>
                        <img className="icon" src={global.imageBaseUrl + 'proposal/media_play.svg'}/>
                    </button>
                }
                <button type="button" className="replay-button" disabled={currentTime == 0} onClick={this.onReplayClick.bind(this)}>
                    <img className="icon" src={global.imageBaseUrl + 'proposal/media_replay.svg'}/>
                </button>
            </div>
        );
    }
}
